import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useOrg } from '../contexts/OrgContext';
import { supabase } from '../lib/supabase';
import { FileText, Plus, CheckCircle2, XCircle, Clock, X, CreditCard, ShoppingCart } from 'lucide-react';
import { Database } from '../types/database.types';
import ModalOverlay from '../components/ui/ModalOverlay';
import { MARKETPLACE_ENABLED } from '../lib/featureFlags';
import {
  EmptyState,
  ErrorState,
  Feedback,
  FeedbackNotice,
  SkeletonList,
  SkeletonRegion,
} from '../components/ui/PageState';

type Request = Database['public']['Tables']['requests']['Row'];

const STATUS_CONFIG: Record<string, { label: string; badge: string; icon: React.ReactNode }> = {
  pending: { label: 'Pendente', badge: 'badge-warning', icon: <Clock size={14} aria-hidden /> },
  approved: { label: 'Aprovado', badge: 'badge-success', icon: <CheckCircle2 size={14} aria-hidden /> },
  rejected: { label: 'Rejeitado', badge: 'badge-error', icon: <XCircle size={14} aria-hidden /> },
  completed: { label: 'Concluído', badge: 'badge-info', icon: <CheckCircle2 size={14} aria-hidden /> },
};

export default function RequestManagement() {
  const { user } = useAuth();
  const { org, role } = useOrg();
  const [requests, setRequests] = useState<Request[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [filterStatus, setFilterStatus] = useState('all');
  const [form, setForm] = useState({ type: 'credit_purchase' as string, title: '', description: '', credits: 50 });
  const [reviewNotes, setReviewNotes] = useState('');
  const [reviewingId, setReviewingId] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<Feedback>(null);

  const isAdminOrFinOps = role === 'admin' || role === 'fin_ops';

  const fetchRequests = useCallback(async () => {
    if (!org) return;
    setLoading(true);
    setLoadError(null);
    let query = supabase.from('requests').select('*').eq('org_id', org.id).order('created_at', { ascending: false });
    if (filterStatus !== 'all') query = query.eq('status', filterStatus);
    const { data, error } = await query;
    if (error) {
      setLoadError(error.message);
    } else {
      setRequests(data || []);
    }
    setLoading(false);
  }, [org, filterStatus]);

  useEffect(() => { fetchRequests(); }, [fetchRequests]);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!org || !user) return;
    setCreating(true);
    try {
      const metadata = form.type === 'credit_purchase' ? { requested_credits: form.credits } : {};
      const { error } = await supabase.from('requests').insert({
        org_id: org.id,
        requester_id: user.id,
        type: form.type,
        title: form.title,
        description: form.description,
        metadata,
      });
      if (error) throw error;

      setFeedback({ type: 'success', message: 'Solicitação enviada. Você será avisado quando for revisada.' });
      setShowCreate(false);
      setForm({ type: 'credit_purchase', title: '', description: '', credits: 50 });
      fetchRequests();
    } catch (err: any) {
      setFeedback({ type: 'error', message: err.message });
    } finally {
      setCreating(false);
    }
  };

  const handleReview = async (id: string, newStatus: 'approved' | 'rejected') => {
    if (!user) return;
    try {
      const { error } = await supabase.from('requests').update({
        status: newStatus,
        reviewer_id: user.id,
        reviewer_notes: reviewNotes,
        reviewed_at: new Date().toISOString(),
      }).eq('id', id);
      if (error) throw error;

      setFeedback({ type: 'success', message: `Solicitação ${newStatus === 'approved' ? 'aprovada' : 'rejeitada'}` });
      setReviewingId(null);
      setReviewNotes('');
      fetchRequests();
    } catch (err: any) {
      setFeedback({ type: 'error', message: err.message });
    }
  };

  const header = (
    <div className="page-header">
      <div>
        <h1>Solicitações</h1>
        <p className="subtitle">{isAdminOrFinOps ? 'Gerencie solicitações da equipe' : 'Solicite créditos para a equipe'}</p>
      </div>
      <button type="button" className="explore-btn is-primary" onClick={() => setShowCreate(true)}>
        <Plus size={18} aria-hidden /> Nova solicitação
      </button>
    </div>
  );

  return (
    <div className="page-container">
      {header}

      <FeedbackNotice feedback={feedback} onDismiss={() => setFeedback(null)} />

      <div className="filter-tabs" role="group" aria-label="Filtrar por status">
        {['all', 'pending', 'approved', 'rejected', 'completed'].map(s => (
          <button
            type="button"
            key={s}
            className={`filter-tab ${filterStatus === s ? 'active' : ''}`}
            aria-pressed={filterStatus === s}
            onClick={() => setFilterStatus(s)}
          >
            {s === 'all' ? 'Todas' : STATUS_CONFIG[s]?.label || s}
          </button>
        ))}
      </div>

      {showCreate && (
        <ModalOverlay label="Nova solicitação" onClose={() => setShowCreate(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Nova solicitação</h2>
              <button type="button" className="modal-close" onClick={() => setShowCreate(false)} aria-label="Fechar">
                <X size={20} aria-hidden />
              </button>
            </div>
            <form onSubmit={handleCreate}>
              <div className="form-group">
                <label htmlFor="req-type">Tipo</label>
                <select id="req-type" className="form-select" value={form.type} onChange={e => setForm({ ...form, type: e.target.value })}>
                  <option value="credit_purchase">Compra de créditos</option>
                  {MARKETPLACE_ENABLED && (
                    <option value="marketplace_purchase">Compra no Marketplace</option>
                  )}
                </select>
              </div>
              <div className="form-group">
                <label htmlFor="req-title">Título</label>
                <input id="req-title" type="text" required value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="Ex: Precisamos de mais créditos" />
              </div>
              <div className="form-group">
                <label htmlFor="req-desc">Descrição (opcional)</label>
                <textarea id="req-desc" className="form-textarea" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="Detalhes da solicitação…" rows={3} />
              </div>
              {form.type === 'credit_purchase' && (
                <div className="form-group">
                  <label htmlFor="req-credits">Quantidade de créditos</label>
                  <input id="req-credits" type="number" inputMode="numeric" min={1} value={form.credits} onChange={e => setForm({ ...form, credits: parseInt(e.target.value) || 0 })} />
                </div>
              )}
              <button type="submit" className="btn-primary" disabled={creating}>{creating ? 'Enviando…' : 'Enviar solicitação'}</button>
            </form>
          </div>
        </ModalOverlay>
      )}

      <div className="glass-card wide-card">
        {loading ? (
          <SkeletonRegion label="Carregando solicitações…"><SkeletonList rows={4} thumb /></SkeletonRegion>
        ) : loadError ? (
          <ErrorState
            flat
            title="Não foi possível carregar as solicitações"
            message="Nenhuma solicitação foi alterada. Tente de novo."
            detail={loadError}
            onRetry={() => void fetchRequests()}
          />
        ) : requests.length === 0 ? (
          <EmptyState
            flat
            icon={<FileText size={24} />}
            title={filterStatus === 'all' ? 'Nenhuma solicitação ainda' : 'Nenhuma solicitação com este status'}
            text={filterStatus === 'all'
              ? 'Pedidos de créditos e compras da equipe aparecem aqui para revisão.'
              : 'Tente outro status para ver o restante.'}
            actions={filterStatus !== 'all' ? (
              <button type="button" className="explore-btn" onClick={() => setFilterStatus('all')}>Mostrar todas as solicitações</button>
            ) : undefined}
          />
        ) : (
          <ul className="requests-list">
            {requests.map(req => {
              const sc = STATUS_CONFIG[req.status] || STATUS_CONFIG.pending;
              const meta = req.metadata as any;
              return (
                <li key={req.id} className="request-card">
                  <div className="request-header">
                    <div className="request-type-icon" aria-hidden>
                      {req.type === 'credit_purchase' ? <CreditCard size={20} /> : <ShoppingCart size={20} />}
                    </div>
                    <div className="request-title-group">
                      <h2 className="request-title">{req.title}</h2>
                      <span className="text-muted text-sm">{req.created_at ? new Date(req.created_at).toLocaleDateString('pt-BR') : ''}</span>
                    </div>
                    <span className={`badge ${sc.badge}`}>{sc.icon} {sc.label}</span>
                  </div>
                  {req.description && <p className="request-desc">{req.description}</p>}
                  {meta?.requested_credits && <p className="text-muted text-sm">Créditos solicitados: <strong>{meta.requested_credits}</strong></p>}
                  {req.reviewer_notes && <p className="reviewer-notes"><span className="sr-only">Observação do revisor: </span>📝 {req.reviewer_notes}</p>}

                  {isAdminOrFinOps && req.status === 'pending' && req.requester_id !== user?.id && (
                    reviewingId === req.id ? (
                      <div className="review-actions">
                        <label htmlFor={`notes-${req.id}`} className="sr-only">Observações da revisão (opcional)</label>
                        <textarea id={`notes-${req.id}`} className="form-textarea" placeholder="Observações da revisão (opcional)…" value={reviewNotes} onChange={e => setReviewNotes(e.target.value)} rows={2} autoFocus />
                        <div className="action-buttons">
                          <button type="button" className="btn-success-sm" onClick={() => handleReview(req.id, 'approved')}>Aprovar pedido</button>
                          <button type="button" className="btn-danger-sm" onClick={() => handleReview(req.id, 'rejected')}>Rejeitar pedido</button>
                          <button type="button" className="btn-muted-sm" onClick={() => setReviewingId(null)}>Cancelar</button>
                        </div>
                      </div>
                    ) : (
                      <button type="button" className="btn-secondary btn-sm" onClick={() => setReviewingId(req.id)}>Revisar pedido</button>
                    )
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
